import React, { useState, useEffect, useRef } from "react";
import { Plus, Trash2, Upload, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { assetUrl } from "@/config";

const BADGES = ["bestseller", "kashmir-special", "deal", "premium", "combo", "festive", "giftbox"];

const slugify = (s) => s.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

const empty = {
  slug: "", name: "", category: "almonds", description: "", origin: "", image: "",
  rating: 4.5, reviews_count: 0, badges: [], in_stock: true,
  weights: [{ label: "250g", grams: 250, price: 0, mrp: 0 }, { label: "500g", grams: 500, price: 0, mrp: 0 }, { label: "1kg", grams: 1000, price: 0, mrp: 0 }],
};

export const ProductFormDialog = ({ open, onClose, product, categories, onSaved }) => {
  const isEdit = !!product;
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  useEffect(() => {
    if (product) setForm({ ...empty, ...product });
    else setForm(empty);
  }, [product, open]);

  if (!open) return null;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setWeight = (i, k, v) => setForm((f) => ({ ...f, weights: f.weights.map((w, idx) => idx === i ? { ...w, [k]: k === "label" ? v : Number(v) } : w) }));
  const addWeight = () => setForm((f) => ({ ...f, weights: [...f.weights, { label: "", grams: 0, price: 0, mrp: 0 }] }));
  const removeWeight = (i) => setForm((f) => ({ ...f, weights: f.weights.filter((_, idx) => idx !== i) }));
  const toggleBadge = (b) => setForm((f) => ({ ...f, badges: f.badges.includes(b) ? f.badges.filter((x) => x !== b) : [...f.badges, b] }));

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("image", data.url);
      toast.success("Image uploaded");
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); }
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Enter a product name");
    if (!form.image.trim()) return toast.error("Add a product image");
    if (form.weights.length === 0) return toast.error("Add at least one weight option");
    const payload = { ...form, slug: isEdit ? form.slug : (form.slug || slugify(form.name)) };
    setSaving(true);
    try {
      if (isEdit) await api.put(`/admin/products/${form.slug}`, payload);
      else await api.post("/admin/products", payload);
      toast.success(isEdit ? "Product updated" : "Product added");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save product");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4 overflow-y-auto" data-testid="product-form-dialog">
      <div className="w-full max-w-2xl rounded-2xl bg-white my-8 max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-[#E7E0D6] px-6 py-4 flex items-center justify-between">
          <h3 className="font-display text-xl font-bold text-[#1F130B]">{isEdit ? "Edit Product" : "Add Product"}</h3>
          <button onClick={onClose} data-testid="product-form-close" className="text-[#6B5B52]"><X size={22} /></button>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <label className="block text-xs font-semibold text-[#6B5B52] mb-1.5">Product Name</label>
              <input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="pf-name" className="w-full rounded-xl border border-[#E7E0D6] bg-[#FAF6F0] px-4 py-2.5 text-sm outline-none focus:border-[#C2410C]" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-[#6B5B52] mb-1.5">Category</label>
              <select value={form.category} onChange={(e) => set("category", e.target.value)} data-testid="pf-category" className="w-full rounded-xl border border-[#E7E0D6] bg-[#FAF6F0] px-4 py-2.5 text-sm outline-none">
                {categories.map((c) => <option key={c.slug} value={c.slug}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-[#6B5B52] mb-1.5">Origin</label>
              <input value={form.origin} onChange={(e) => set("origin", e.target.value)} data-testid="pf-origin" className="w-full rounded-xl border border-[#E7E0D6] bg-[#FAF6F0] px-4 py-2.5 text-sm outline-none focus:border-[#C2410C]" />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs font-semibold text-[#6B5B52] mb-1.5">Description</label>
              <textarea value={form.description} onChange={(e) => set("description", e.target.value)} rows={2} data-testid="pf-description" className="w-full rounded-xl border border-[#E7E0D6] bg-[#FAF6F0] px-4 py-2.5 text-sm outline-none focus:border-[#C2410C] resize-none" />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-[#6B5B52] mb-1.5">Product Image</label>
            <div className="flex items-center gap-3">
              {form.image && <img src={assetUrl(form.image)} alt="" className="h-16 w-16 rounded-lg object-cover border border-[#E7E0D6]" />}
              <input value={form.image} onChange={(e) => set("image", e.target.value)} placeholder="Image URL or upload →" data-testid="pf-image" className="flex-1 rounded-xl border border-[#E7E0D6] bg-[#FAF6F0] px-4 py-2.5 text-sm outline-none focus:border-[#C2410C]" />
              <input ref={fileRef} type="file" accept="image/*" hidden onChange={(e) => upload(e.target.files[0])} />
              <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="pf-upload" className="rounded-xl border-2 border-[#D97706] text-[#9A3412] px-3 py-2.5 disabled:opacity-60">{uploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}</button>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-semibold text-[#6B5B52] mb-1.5">Rating</label>
              <input type="number" step="0.1" min="0" max="5" value={form.rating} onChange={(e) => set("rating", Number(e.target.value))} data-testid="pf-rating" className="w-full rounded-xl border border-[#E7E0D6] bg-[#FAF6F0] px-4 py-2.5 text-sm outline-none" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-[#6B5B52] mb-1.5">Reviews Count</label>
              <input type="number" min="0" value={form.reviews_count} onChange={(e) => set("reviews_count", Number(e.target.value))} data-testid="pf-reviews" className="w-full rounded-xl border border-[#E7E0D6] bg-[#FAF6F0] px-4 py-2.5 text-sm outline-none" />
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 text-sm font-medium text-[#1F130B]">
                <input type="checkbox" checked={form.in_stock} onChange={(e) => set("in_stock", e.target.checked)} data-testid="pf-instock" className="h-4 w-4" /> In Stock
              </label>
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-[#6B5B52] mb-2">Badges</label>
            <div className="flex flex-wrap gap-2">
              {BADGES.map((b) => (
                <button key={b} onClick={() => toggleBadge(b)} data-testid={`pf-badge-${b}`} className={`text-xs font-semibold px-3 py-1.5 rounded-full border capitalize ${form.badges.includes(b) ? "bg-[#C2410C] text-white border-[#C2410C]" : "bg-white text-[#6B5B52] border-[#E7E0D6]"}`}>{b.replace("-", " ")}</button>
              ))}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-semibold text-[#6B5B52]">Weight Options & Prices (₹)</label>
              <button onClick={addWeight} data-testid="pf-add-weight" className="text-xs font-semibold text-[#C2410C] flex items-center gap-1"><Plus size={13} /> Add</button>
            </div>
            <div className="space-y-2">
              {form.weights.map((w, i) => (
                <div key={i} className="flex gap-2 items-center" data-testid={`pf-weight-${i}`}>
                  <input value={w.label} onChange={(e) => setWeight(i, "label", e.target.value)} placeholder="Label (250g)" className="w-24 rounded-lg border border-[#E7E0D6] bg-[#FAF6F0] px-3 py-2 text-sm outline-none" />
                  <input type="number" value={w.grams} onChange={(e) => setWeight(i, "grams", e.target.value)} placeholder="grams" className="w-20 rounded-lg border border-[#E7E0D6] bg-[#FAF6F0] px-3 py-2 text-sm outline-none" />
                  <input type="number" value={w.price} onChange={(e) => setWeight(i, "price", e.target.value)} placeholder="Price" className="flex-1 rounded-lg border border-[#E7E0D6] bg-[#FAF6F0] px-3 py-2 text-sm outline-none" />
                  <input type="number" value={w.mrp} onChange={(e) => setWeight(i, "mrp", e.target.value)} placeholder="MRP" className="flex-1 rounded-lg border border-[#E7E0D6] bg-[#FAF6F0] px-3 py-2 text-sm outline-none" />
                  <button onClick={() => removeWeight(i)} className="text-[#9CA3AF] hover:text-[#C2410C]"><Trash2 size={16} /></button>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="sticky bottom-0 bg-white border-t border-[#E7E0D6] px-6 py-4 flex justify-end gap-3">
          <button onClick={onClose} className="rounded-full border-2 border-[#E7E0D6] text-[#6B5B52] font-semibold px-6 py-2.5">Cancel</button>
          <button onClick={save} disabled={saving} data-testid="pf-save" className="inline-flex items-center gap-2 rounded-full bg-[#C2410C] hover:bg-[#9A3412] text-white font-semibold px-6 py-2.5 disabled:opacity-60">
            {saving ? <Loader2 size={16} className="animate-spin" /> : null} {isEdit ? "Save Changes" : "Add Product"}
          </button>
        </div>
      </div>
    </div>
  );
};
